import {
  type ImagePickerAsset,
  type ImagePickerOptions,
  launchImageLibraryAsync,
} from "expo-image-picker";
import { useCallback, useState } from "react";

import {
  type PickerNoResponseInfo,
  startPickerNoResponseTimer,
} from "./pickerNoResponse";

export type UseImagePickerOptions = {
  /**
   * Fires once the user finishes selecting images. Return a `Promise` to keep
   * `isLoading` true until your upload work settles; the hook awaits it inside
   * its own `try/finally` and clears loading when the promise resolves.
   */
  onImageSelected: (assets: ImagePickerAsset[]) => Promise<void> | void;
  /** The user dismissed the picker without choosing anything. */
  onCanceled?: () => void;
  /** The picker threw. Without this the failure is only a console line. */
  onError?: (error: unknown) => void;
  /** The picker has not answered yet; see PICKER_NO_RESPONSE_TIMEOUT_MS. */
  onNoResponse?: (info: PickerNoResponseInfo) => void;
};

export type UseImagePickerResult = {
  /** Opens the OS image library; resolves once the picker is dismissed. */
  pickImage: () => Promise<void>;
  /** True from `pickImage` invocation until `onImageSelected` settles. */
  isLoading: boolean;
};

const imagePickerOptions: ImagePickerOptions = {
  allowsEditing: false,
  mediaTypes: ["images"],
  allowsMultipleSelection: true,
  // Default compression
  // 0 means compress for small size, 1 means compress for maximum quality
  quality: 0.8,
};

/**
 * Wraps `expo-image-picker`'s `launchImageLibraryAsync`. Owns its own
 * `isLoading` state and awaits the consumer's `onImageSelected` callback so
 * the loading affordance stays up while uploads complete.
 */
export const useImagePicker = ({
  onImageSelected,
  onCanceled,
  onError,
  onNoResponse,
}: UseImagePickerOptions): UseImagePickerResult => {
  const [isLoading, setIsLoading] = useState(false);

  const pickImage = useCallback(async () => {
    setIsLoading(true);
    const cancelNoResponseTimer = startPickerNoResponseTimer(onNoResponse);
    try {
      const result = await launchImageLibraryAsync(imagePickerOptions);
      // Cleared on the picker's answer, not on `onImageSelected` settling —
      // the consumer's upload work can legitimately outrun the timeout.
      cancelNoResponseTimer();
      if (result.canceled) {
        onCanceled?.();
        return;
      }
      await onImageSelected(result.assets);
    } catch (error) {
      cancelNoResponseTimer();
      console.error("Error picking image", error);
      onError?.(error);
    } finally {
      setIsLoading(false);
    }
  }, [onImageSelected, onCanceled, onError, onNoResponse]);

  return { pickImage, isLoading };
};
